import { z } from "zod";
import type { TFunction } from "i18next";

/**
 * Auth schema factories.
 *
 * Each factory receives the `t()` function so validation messages
 * are localized. Call them inside components/hooks where `useTranslation`
 * is available.
 *
 * @example
 * const { t } = useTranslation();
 * const schema = createLoginSchema(t);
 */

// ---------------------------------------------------------------------------
// LOGIN
// ---------------------------------------------------------------------------

export function createLoginSchema(t: TFunction) {
  return z.object({
    email: z.string().min(1, t("validation.emailRequired")).email(t("validation.emailInvalid")),
    password: z.string().min(1, t("validation.passwordRequired")),
  });
}

export type LoginValues = z.infer<ReturnType<typeof createLoginSchema>>;

// ---------------------------------------------------------------------------
// REGISTER
// ---------------------------------------------------------------------------

const REGISTER_NAME_MAX_CODE_POINTS = 255;

function hasAtMostCodePoints(value: string, maximum: number) {
  const codePoints = value[Symbol.iterator]();

  for (let count = 0; count <= maximum; count += 1) {
    if (codePoints.next().done) {
      return true;
    }
  }

  return false;
}

export function createRegisterSchema(t: TFunction) {
  return z.object({
    name: z
      .string()
      .min(1, t("validation.nameRequired"))
      .refine(
        (name) => hasAtMostCodePoints(name, REGISTER_NAME_MAX_CODE_POINTS),
        t("validation.nameMaxLength"),
      ),
    email: z.string().min(1, t("validation.emailRequired")).email(t("validation.emailInvalid")),
    password: z
      .string()
      .min(12, t("validation.passwordMinLength"))
      .max(128, t("validation.passwordMaxLength")),
  });
}

export type RegisterValues = z.infer<ReturnType<typeof createRegisterSchema>>;

// ---------------------------------------------------------------------------
// FORGOT PASSWORD
// ---------------------------------------------------------------------------

export function createForgotPasswordSchema(t: TFunction) {
  return z.object({
    email: z.string().min(1, t("validation.emailRequired")).email(t("validation.emailInvalid")),
  });
}

export type ForgotPasswordValues = z.infer<ReturnType<typeof createForgotPasswordSchema>>;

// ---------------------------------------------------------------------------
// RESET PASSWORD
// ---------------------------------------------------------------------------

export function createResetPasswordSchema(t: TFunction) {
  return z
    .object({
      password: z
        .string()
        .min(12, t("validation.passwordMinLength"))
        .max(128, t("validation.passwordMaxLength")),
      confirmPassword: z.string().min(1, t("validation.confirmPasswordRequired")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("validation.passwordsMismatch"),
      path: ["confirmPassword"],
    });
}

export type ResetPasswordValues = z.infer<ReturnType<typeof createResetPasswordSchema>>;

export function createChangePasswordSchema(t: TFunction) {
  return z
    .object({
      currentPassword: z.string().min(1, t("validation.currentPasswordRequired")),
      newPassword: z
        .string()
        .min(12, t("validation.passwordMinLength"))
        .max(128, t("validation.passwordMaxLength")),
      confirmPassword: z.string().min(1, t("validation.confirmPasswordRequired")),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t("validation.passwordsMismatch"),
      path: ["confirmPassword"],
    })
    .refine((data) => data.newPassword === "" || data.newPassword !== data.currentPassword, {
      message: t("validation.newPasswordSameAsCurrent"),
      path: ["newPassword"],
    });
}

export type ChangePasswordValues = z.infer<ReturnType<typeof createChangePasswordSchema>>;
